import type NumberUnit from '@exodus/currency';
// eslint-disable-next-line @exodus/restricted-imports/prefer-basic-utils
import { keyBy, memoize, orderBy } from 'lodash';
import { createSelector } from 'reselect';

import type { Asset, Assets } from '@/types';
import selectors from '../../selectors.js';

type AssetWithSortedMetadata = Asset & {
  sortMetadata: {
    displayName: string;
    fiatBalance: NumberUnit;
    isBaseAsset: boolean;
    enabled: boolean;
  };
};

type SortingDirection = 'asc' | 'desc';

export type Sorting = [
  keyof AssetWithSortedMetadata['sortMetadata'],
  SortingDirection,
][];

const createSortedAssetsSelectorFactory = (assetSelector) => {
  return memoize((sorting: Sorting) => {
    const sortingMap = sorting.map(([property, order]) => [
      (a: AssetWithSortedMetadata) => a.sortMetadata[property],
      order,
    ]);

    const sortKeys = sortingMap.map((o) => o[0]);
    const sortOrder = sortingMap.map((o) => o[1]) as SortingDirection[];

    return createSelector(
      assetSelector,
      selectors.fiatBalances.byAssetInActiveAccount,
      selectors.enabledAssets.getIsEnabled,
      (assets: Assets, fiatBalances, getIsEnabled) => {
        const assetsMap = keyBy(assets, 'name');

        const assetsWithMetaData: AssetWithSortedMetadata[] = Object.values(
          assets,
        ).map((asset: Asset) => {
          return {
            ...asset,
            sortMetadata: {
              displayName: asset.displayName,
              fiatBalance: fiatBalances?.[asset.name],
              isBaseAsset: asset.baseAssetName === asset.name,
              enabled: getIsEnabled(asset.name),
            },
          } satisfies AssetWithSortedMetadata;
        });

        const sorted = orderBy(assetsWithMetaData, sortKeys, sortOrder);
        return sorted.map(
          (assetWithMetaData) => assetsMap[assetWithMetaData.name],
        );
      },
    );
  });
};

export default createSortedAssetsSelectorFactory;
